import React, { useState, useRef, useEffect } from 'react';
import { AppBar, Toolbar, Typography, Box, IconButton, Menu, MenuItem, Avatar, Chip, Divider, Tooltip } from '@mui/material';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon, MonetizationOn as MonetizationOnIcon, Flag as FlagIcon, CardGiftcard as CardGiftcardIcon, Settings as SettingsIcon, Logout as LogoutIcon, ShowChart as ShowChartIcon, WbSunny as WbSunnyIcon, Stars as StarsIcon, MoreHoriz as MoreHorizIcon } from '@mui/icons-material';
import { useUser } from '../context/UserContext';
import { useAuth } from '../context/AuthContext';
import LevelDisplay from './LevelDisplay';

const TAB_WIDTH = 44; // Approximate width of each tab (icon + padding)
const ELLIPSIS_WIDTH = 44; // Width reserved for ellipsis button

const Navigation = () => {
  const location = useLocation();
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const { logout, userType } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [overflowAnchorEl, setOverflowAnchorEl] = useState<null | HTMLElement>(null);
  const [visibleTabCount, setVisibleTabCount] = useState(6);
  const tabsContainerRef = useRef<HTMLDivElement>(null);

  const isKidUser = userType === 'kid';

  const getTabIndex = () => {
    if (location.pathname.includes('/targets')) return 0;
    if (location.pathname.includes('/points')) return 1;
    if (location.pathname.includes('/energy')) return 2;
    if (location.pathname.includes('/levels')) return 3;
    if (location.pathname.includes('/statistics')) return isKidUser ? 4 : 4;
    if (location.pathname.includes('/manage') || location.pathname.includes('/gifts')) return isKidUser ? -1 : 5;
    return 0;
  };

  const isLevelsPage = () => {
    return location.pathname.includes('/levels');
  };

  const isEnergyPage = () => {
    return location.pathname.includes('/energy');
  };

  const isBadgesPage = () => {
    return location.pathname.includes('/badges');
  };

  const isGiftsPage = () => {
    return location.pathname.includes('/gifts');
  };

  const isStatisticsPage = () => {
    return location.pathname.includes('/statistics');
  };

  const handleBackClick = () => {
    if (isLevelsPage() || isEnergyPage() || isBadgesPage() || isStatisticsPage()) {
      // From levels, energy, badges, or statistics page, go back to previous page in history
      navigate(-1);
    } else if (isGiftsPage() && userId) {
      // From gifts page, go back to manage
      navigate(`/user/${userId}/manage`);
    } else {
      // From any other page (including manage), go to select-kid
      navigate('/select-kid');
    }
  };

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSwitchKid = () => {
    handleUserMenuClose();
    navigate('/select-kid');
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
  };

  const handleOverflowClick = (event: React.MouseEvent<HTMLElement>) => {
    setOverflowAnchorEl(event.currentTarget);
  };

  const handleOverflowClose = () => {
    setOverflowAnchorEl(null);
  };

  // Define tabs based of user type
  const tabs = isKidUser
    ? [
        // Kid-only tabs (no manage, no switch kid)
        { label: 'Targets', path: `/user/${userId}/targets`, icon: <FlagIcon sx={{ fontSize: 18 }} /> },
        { label: 'Gifts', path: `/user/${userId}/points`, icon: <CardGiftcardIcon sx={{ fontSize: 18 }} /> },
        { label: 'Energy', path: `/user/${userId}/energy`, icon: <WbSunnyIcon sx={{ fontSize: 18 }} /> },
        { label: 'Levels', path: `/user/${userId}/levels`, icon: <StarsIcon sx={{ fontSize: 18 }} /> },
        { label: 'Stats', path: `/user/${userId}/statistics`, icon: <ShowChartIcon sx={{ fontSize: 18 }} /> },
      ]
    : [
        // Parent tabs
        { label: 'Targets', path: `/user/${userId}/targets`, icon: <FlagIcon sx={{ fontSize: 18 }} /> },
        { label: 'Gifts', path: `/user/${userId}/points`, icon: <CardGiftcardIcon sx={{ fontSize: 18 }} /> },
        { label: 'Energy', path: `/user/${userId}/energy`, icon: <WbSunnyIcon sx={{ fontSize: 18 }} /> },
        { label: 'Levels', path: `/user/${userId}/levels`, icon: <StarsIcon sx={{ fontSize: 18 }} /> },
        { label: 'Stats', path: `/user/${userId}/statistics`, icon: <ShowChartIcon sx={{ fontSize: 18 }} /> },
        { label: 'Manage', path: `/user/${userId}/manage`, icon: <SettingsIcon sx={{ fontSize: 18 }} /> },
      ];

  // Calculate visible tabs based on container width
  useEffect(() => {
    const calculateVisibleTabs = () => {
      if (!tabsContainerRef.current) return;

      const containerWidth = tabsContainerRef.current.offsetWidth;
      const totalTabs = tabs.length;

      // Calculate how many tabs can fit
      // Reserve space for ellipsis button if not all tabs fit
      const availableWidth = containerWidth;
      const tabsThatFit = Math.floor(availableWidth / TAB_WIDTH);

      if (tabsThatFit >= totalTabs) {
        setVisibleTabCount(totalTabs);
      } else {
        // Reserve space for ellipsis button
        const tabsWithEllipsis = Math.floor((availableWidth - ELLIPSIS_WIDTH) / TAB_WIDTH);
        setVisibleTabCount(Math.max(1, tabsWithEllipsis)); // Show at least 1 tab
      }
    };

    calculateVisibleTabs();

    const resizeObserver = new ResizeObserver(calculateVisibleTabs);
    if (tabsContainerRef.current) {
      resizeObserver.observe(tabsContainerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [tabs.length]);

  const currentTab = getTabIndex();

  if (!userId) return null;

  return (
    <AppBar
      position="sticky"
      sx={{
        top: 0,
        zIndex: 1100,
        bgcolor: 'primary.main',
        borderRadius: 3,
        boxShadow: '0 10px 40px -10px rgba(99, 102, 241, 0.4)',
      }}
    >
      <Toolbar sx={{ gap: 1, minHeight: 56 }}>
        <IconButton
          edge="start"
          color="inherit"
          onClick={handleBackClick}
          sx={{
            bgcolor: 'rgba(255,255,255,0.1)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
          }}
          title={isGiftsPage() ? "Back to Manage" : isLevelsPage() || isStatisticsPage() ? "Back" : "Switch Kid"}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>

        <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: 0.5, display: { xs: 'none', sm: 'block' } }}>
          Points
        </Typography>

        <Box ref={tabsContainerRef} sx={{ display: 'flex', gap: 0.5, ml: 1, flex: 1, minWidth: 0, overflow: 'hidden' }}>
          {tabs.slice(0, visibleTabCount).map((tab, index) => (
            <Box
              key={tab.path}
              component={Link}
              to={tab.path}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.75,
                borderRadius: 1.5,
                textDecoration: 'none',
                color: 'white',
                fontWeight: 500,
                fontSize: '0.85rem',
                transition: 'all 0.2s ease',
                bgcolor: currentTab === index ? 'rgba(255,255,255,0.2)' : 'transparent',
                '&:hover': {
                  bgcolor: currentTab === index ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)',
                },
                flexShrink: 0,
              }}
            >
              {tab.icon}
              <Typography variant="body2" sx={{ fontWeight: 600, display: { xs: 'none', md: 'block' } }}>
                {tab.label}
              </Typography>
            </Box>
          ))}

          {/* Overflow menu button */}
          {visibleTabCount < tabs.length && (
            <>
              <IconButton
                onClick={handleOverflowClick}
                color="inherit"
                size="small"
                sx={{
                  bgcolor: tabs.slice(visibleTabCount).some((_, i) => currentTab === visibleTabCount + i)
                    ? 'rgba(255,255,255,0.2)'
                    : 'transparent',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.15)',
                  },
                  flexShrink: 0,
                }}
              >
                <MoreHorizIcon fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={overflowAnchorEl}
                open={Boolean(overflowAnchorEl)}
                onClose={handleOverflowClose}
                PaperProps={{
                  sx: { borderRadius: 2, minWidth: 140, mt: 1 }
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                {tabs.slice(visibleTabCount).map((tab, i) => {
                  const tabIndex = visibleTabCount + i;
                  const isActive = currentTab === tabIndex;
                  return (
                    <MenuItem
                      key={tab.path}
                      component={Link}
                      to={tab.path}
                      onClick={handleOverflowClose}
                      sx={{
                        gap: 1.5,
                        fontWeight: isActive ? 600 : 400,
                        bgcolor: isActive ? 'rgba(99, 102, 241, 0.08)' : 'transparent',
                        '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.12)' },
                      }}
                    >
                      {tab.icon}
                      {tab.label}
                    </MenuItem>
                  );
                })}
              </Menu>
            </>
          )}
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Chip
              icon={<MonetizationOnIcon sx={{ fontSize: 16 }} />}
              label={user.coins}
              size="small"
              onClick={() => userId && navigate(`/user/${userId}/points`)}
              sx={{
                bgcolor: 'rgba(245, 158, 11, 0.3)',
                color: 'white',
                fontWeight: 600,
                fontSize: '0.8rem',
                height: 28,
                display: { xs: 'none', sm: 'flex' },
                cursor: 'pointer',
                '& .MuiChip-icon': { color: '#fbbf24' },
                '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.5)' }
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                onClick={handleUserMenuClick}
                color="inherit"
                size="small"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 0.5 }}
              >
                <Avatar sx={{ width: 28, height: 28, bgcolor: 'secondary.main', fontWeight: 600, fontSize: '0.9rem' }}>
                  {user.name.charAt(0).toUpperCase()}
                </Avatar>
                <Typography variant="body2" sx={{ fontWeight: 500, display: { xs: 'none', md: 'block' } }}>
                  {user.name}
                </Typography>
              </IconButton>
              <Tooltip title={`${user.totalPoints} points`}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LevelDisplay level={user.level ?? 0} size="small" />
                </Box>
              </Tooltip>
            </Box>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleUserMenuClose}
              PaperProps={{
                sx: { borderRadius: 2, minWidth: 150 }
              }}
            >
              {!isKidUser && (
                <>
                  <MenuItem onClick={handleSwitchKid} sx={{ fontWeight: 500 }}>
                    Switch Kid
                  </MenuItem>
                  <Divider />
                </>
              )}
              <MenuItem onClick={handleLogout} sx={{ fontWeight: 500, color: 'error.main' }}>
                <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navigation;